import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Inbox from "../components/Inbox";
import ApprovalInbox from "../components/ApprovalInbox";
import EmailDetails from "../components/EmailDetails";
import ApprovalEmailDetail from "../components/ApprovalEmailDetail";
import UploadModal from "../components/UploadModal";
import { getEmails, getEmailDetails } from "../api";
import "./Dashboard.css";

export default function Dashboard() {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");

  const [showUploadModal, setShowUploadModal] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const departmentData = JSON.parse(localStorage.getItem("department")) || {};
  const isSuperuser = departmentData.is_superuser || false;

  const loadEmails = async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getEmails();
      setEmails(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      if (!isBackground) setError(err.message);
    }
    if (!isBackground) setLoading(false);
  };

  useEffect(() => {
    loadEmails();
    const interval = setInterval(() => loadEmails(true), 30000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  // Auto-open email from query param (e.g., from audit log)
  useEffect(() => {
    const emailId = searchParams.get("email");
    if (emailId) {
      handleSelectEmail({ id: parseInt(emailId, 10) });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSelectEmail(email) {
    if (!email) return;
    setSelectedEmail(null);
    setLoading(true);
    try {
      const detailed = await getEmailDetails(email.id);
      setSelectedEmail(detailed);
      // Mark as read locally so the inbox updates immediately
      setEmails((prev) =>
        prev.map((e) => (e.id === email.id ? { ...e, is_read: true } : e))
      );
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function handleActionComplete(updatedEmailId) {
    if (updatedEmailId) {
      try {
        const updated = await getEmailDetails(updatedEmailId);
        setSelectedEmail(updated);
      } catch {
        setSelectedEmail(null);
      }
    } else {
      setSelectedEmail(null);
    }
    loadEmails();
  }

  const filteredEmails = emails.filter((email) => {
    if (statusFilter === "archived" && !email.is_archived) return false;
    if (statusFilter === "active" && email.is_archived) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchSubject = (email.subject || "").toLowerCase().includes(q);
      const matchFrom = (email.from_email || "").toLowerCase().includes(q);
      const matchDept = (email.department_name || "").toLowerCase().includes(q);
      const matchUrgency = (email.urgency || "").toLowerCase().includes(q);
      if (!matchSubject && !matchFrom && !matchDept && !matchUrgency) return false;
    }

    return true;
  });

  return (
    <div className="dash-root">
      <Header />
      <div className="dash-layout">
        <Sidebar activePage="inbox" isSuperuser={isSuperuser} />

        <aside className="dash-inbox-panel">
          <div className="dash-search-box">
            <input
              type="text"
              placeholder="ძიება..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
              <button
                className="dash-upload-btn"
                onClick={() => setShowUploadModal(true)}
                title="დოკუმენტის ატვირთვა"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </button>
          </div>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="active">აქტიური</option>
            <option value="archived">არქივი</option>
            <option value="all">ყველა</option>
            {isSuperuser && <option value="pending">მოლოდინში</option>}
          </select>

          <div className="email-list-scroll">
            {isSuperuser && statusFilter === "pending" ? (
              <ApprovalInbox
                onSelectEmail={handleSelectEmail}
                selectedEmailId={selectedEmail?.id}
              />
            ) : (
              <Inbox
                emails={filteredEmails}
                onSelectEmail={handleSelectEmail}
                selectedEmailId={selectedEmail?.id}
              />
            )}
          </div>
        </aside>

        <section className="dash-content-panel">
          {loading && !selectedEmail && (
            <div className="dash-center-msg">
              <div className="dash-loading-spinner" />
              <span>იტვირთება...</span>
            </div>
          )}
          {error && <div className="dash-error-box">{error}</div>}
          {!loading && !error && (
            <>
              {isSuperuser && statusFilter === "pending" ? (
                <ApprovalEmailDetail
                  email={selectedEmail}
                  onActionComplete={handleActionComplete}
                />
              ) : (
                <EmailDetails
                  key={selectedEmail?.id + "-" + selectedEmail?.is_archived}
                  email={selectedEmail}
                  isSuperuser={isSuperuser}
                  onActionComplete={handleActionComplete}
                />
              )}
            </>
          )}
        </section>
      </div>

      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            loadEmails();
          }}
        />
      )}
    </div>
  );
}
